import { Check, X } from "lucide-react";
import { cn } from "@nutrayan/ui";
import {
  assessPassword,
  rulesFor,
  usePasswordPolicy,
  type PasswordPolicy,
} from "@/lib/password-policy";

interface Props {
  value: string;
  policy?: PasswordPolicy;
}

const BAR_COLORS = ["bg-charcoal/15", "bg-terracotta", "bg-gold", "bg-indigo", "bg-forest"];

/** Live password strength meter + requirement checklist. Mirrors the server policy
 * fetched from /api/v1/auth/admin/password/policy (BLK-API-017). */
export function PasswordStrength({ value, policy: policyProp }: Props) {
  const livePolicy = usePasswordPolicy();
  const policy = policyProp ?? livePolicy;
  const rules = rulesFor(policy);
  const { satisfied, score, strengthLabel } = assessPassword(value, policy);

  return (
    <div className="mt-2 space-y-2" aria-live="polite">
      <div className="flex items-center gap-2">
        <div className="flex h-1.5 flex-1 gap-1" aria-hidden>
          {[0, 1, 2, 3].map((i) => (
            <span
              key={i}
              className={cn(
                "flex-1 rounded-full transition-colors",
                i < score ? BAR_COLORS[score] : "bg-charcoal/10",
              )}
            />
          ))}
        </div>
        {value ? (
          <span className="text-caption text-muted w-12 text-right">{strengthLabel}</span>
        ) : null}
      </div>
      <ul className="grid gap-1">
        {rules.map((rule) => {
          const ok = satisfied[rule.id];
          return (
            <li
              key={rule.id}
              className={cn(
                "text-caption flex items-center gap-1.5",
                ok ? "text-forest" : "text-muted",
              )}
            >
              {ok ? (
                <Check size={13} aria-hidden className="shrink-0" />
              ) : (
                <X size={13} aria-hidden className="shrink-0" />
              )}
              {rule.label}
            </li>
          );
        })}
      </ul>
    </div>
  );
}
